import { LineChart, LineChartFactory, LineChartProps } from '@mantine/charts'
import { BoxProps, StylesApiProps } from '@mantine/core'
import { InView, type useInView } from '@suspensive/react-dom'
import { useMemo } from 'react'

type InViewOptions = Parameters<typeof useInView>[0]

interface SparklineProps extends StylesApiProps<LineChartFactory>, BoxProps {
  data: number[]
  c?: string
  /**
   * @default true
   * @note When `true` (by default), it requires parent to be a client component (restricted by InView).
   */
  renderInView?: boolean
  inViewOptions?: InViewOptions
  fallback?: React.ReactNode
  isLoading?: boolean
}

const trendColors = {
  positive: 'var(--common-success)',
  negative: 'var(--common-danger)',
  neutral: 'var(--content-secondary)',
}

const lineChartDefaultProps: Partial<LineChartProps> = {
  withYAxis: false,
  withXAxis: false,
  withDots: false,
  withTooltip: false,
  gridAxis: 'none',
  strokeWidth: 1,
  curveType: 'linear',
  yAxisProps: {
    domain: ['dataMin', 'dataMax'],
  },
}

export default function Sparkline({
  renderInView = true,
  inViewOptions,
  fallback,
  data,
  c,
  isLoading = false,
  ...props
}: SparklineProps) {
  const lineData = useMemo(
    () =>
      data.map((value, index) => ({
        value,
        index,
      })),
    [data],
  )
  const color = useMemo(() => {
    if (c) return c
    if (data.length < 2) return trendColors.neutral
    const first = data[0]
    const last = data[data.length - 1]
    if (last > first) return trendColors.positive
    if (last < first) return trendColors.negative
    return trendColors.neutral
  }, [c, data])
  const lineChartProps: LineChartProps = {
    ...lineChartDefaultProps,
    ...props,
    data: lineData,
    dataKey: 'index',
    series: [
      {
        name: 'value',
        color,
      },
    ],
  }

  if (renderInView) {
    return (
      <InView triggerOnce {...inViewOptions}>
        {({ ref, isInView }) => (
          <div ref={ref}>
            {isInView && !isLoading ? <LineChart {...lineChartProps} /> : fallback}
          </div>
        )}
      </InView>
    )
  }

  return <LineChart {...lineChartProps} />
}
